/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Cell, Grid, HFlow, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { CheckboxField, DateField, Form, FormRenderProps, NumberField, SubmitButton, TimeField } from 'components/form'
import { CidSelectField } from 'components/form/field/select/CidSelectField/CidSelectField'
import { resolveValue } from 'components/form/final-form/hooks/useField'
import { confirm } from 'components/modals/confirm'
import { subDays } from 'date-fns'
import { FormApi } from 'final-form'
import createDecorator from 'final-form-calculate'
import { TipoAtestadoEnum } from 'graphql/types.generated'
import { Fragment, useState } from 'react'
import { dateAsYyyyMmDd, formatHoursMinutes, toDate } from 'util/date/formatDate'
import { onlyNumbersNonZeroLeft } from 'util/mask'
import { metaPath } from 'util/metaPath'

import { AtestadoAssinaturaDigitalView } from '../assinatura-digital/AtestadoAssinaturaDigitalView'
import { AtestadoConfirmarAssinaturaDigitalModal } from '../assinatura-digital/AtestadoConfirmarAssinaturaDigitalModal'
import { MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED } from '../assinatura-digital/model-atestadoAssinaturaDigital'
import { useAtestadoAssinaturaDigitalForm } from '../assinatura-digital/useAtestadoAssinaturaDigitalForm'
import AtestadoModel, { CidadaoAtestado } from '../components/model-atestado'
import AtestadoPadraoTextBox, { getAtestadoPadraoText } from './AtestadoPadraoTextBox'
import { atestadoPadraoFormValidator } from './validator-atestadoPadrao'

export const name = metaPath<AtestadoModel>()

export interface AtestadoPadraoFormProps {
  cidadao: CidadaoAtestado
  dataAtendimento: Instant
  onSubmit(values: AtestadoModel): void
  atendimentoId: ID
  prontuarioId: ID
}

export const cpfCnsDecorator = createDecorator({
  field: name.naoPossui.absolutePath(),
  updates: {
    [name.cpfCns.absolutePath()]: (value: boolean, allValues: AtestadoModel) => {
      if (value) return (allValues.cpfCns = '')
      else return allValues.cpfCns
    },
  },
})

export default function AtestadoPadraoForm(props: AtestadoPadraoFormProps) {
  const { cidadao, dataAtendimento, onSubmit, atendimentoId, prontuarioId } = props
  const { acesso } = useAcessoLotacaoOrEstagio()

  const nomeUnidadeSaude = acesso?.unidadeSaude.nome
  const possuiCpfCns = cidadao.cpf || cidadao.cns

  const alert = useAlert()
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [isEmailCidadaoDirty, setIsEmailCidadaoDirty] = useState(false)
  const [isEmailCidadaoCanceled, setIsEmailCidadaoCanceled] = useState(false)

  const tipoAtestado = TipoAtestadoEnum.PADRAO

  const handleSubmit = (values: AtestadoModel, formApi: FormApi) => {
    onSubmit({ ...values, tipo: tipoAtestado })
    setTimeout(formApi.reset)
    formApi.getRegisteredFields().forEach((item) => formApi.resetFieldState(item))
    alert('success', 'Atestado salvo com sucesso.')
  }

  const {
    emailCidadao,
    handleSubmitWrapper,
    isAssinando,
    isModalOpen,
    onConfirm,
    onCancel,
    errorMessage,
    onAlertClose,
  } = useAtestadoAssinaturaDigitalForm({
    cidadaoId: cidadao.id,
    atendimentoId,
    prontuarioId,
    tipoAtestado,
    dataAtendimento,
    onSubmit: handleSubmit,
  })

  const handleCancel = (formProps: FormRenderProps<AtestadoModel>) => {
    if (formProps.dirty || isEmailCidadaoDirty) {
      confirm({
        title: 'Deseja cancelar a inclusão?',
        body: 'As alterações realizadas serão perdidas.',
        cancelLabel: 'Não, continuar inclusão',
        confirmLabel: 'Sim, cancelar inclusão',
        onConfirm: () => {
          alert('success', 'Cadastro de atestado cancelado com sucesso.')
          setIsEmailCidadaoCanceled(true)
          setTimeout(() => formProps.form.reset())
        },
      })()
    } else {
      alert('success', 'Cadastro de atestado cancelado com sucesso.')
      setIsEmailCidadaoCanceled(true)
    }
    formProps.form.getRegisteredFields().forEach((item) => formProps.form.resetFieldState(item))
  }

  const renderForm = (formProps: FormRenderProps<AtestadoModel>) => {
    const values = resolveValue(formProps.values, name) || ({} as AtestadoModel)
    values.descricao = getAtestadoPadraoText({ cidadao, values, nomeUnidadeSaude })

    return (
      <Fragment>
        {!possuiCpfCns && (
          <Cell size={8}>
            <Grid gap={1}>
              <Cell size={4}>
                <NumberField
                  name={name.cpfCns}
                  label='CPF/CNS'
                  disabled={values.naoPossui}
                  required={!values.naoPossui}
                  maxLength={15}
                />
              </Cell>
              <Cell>
                <HFlow style={styles.naoPossuiContainer}>
                  <CheckboxField name={name.naoPossui} label='Não possui' />
                </HFlow>
              </Cell>
            </Grid>
          </Cell>
        )}
        <Cell md={4} lg={3}>
          <DateField
            name={name.data.absolutePath()}
            label='Data'
            required
            initialValue={dateAsYyyyMmDd(dataAtendimento)}
            minDate={subDays(dataAtendimento, 90)}
            maxDate={toDate(dataAtendimento)}
          />
        </Cell>
        <Cell size={2}>
          <TimeField
            name={name.hora}
            label='Hora'
            placeholder='hh:mm'
            required
            initialValue={formatHoursMinutes(dataAtendimento)}
          />
        </Cell>
        <Cell size={2}>
          <NumberField name={name.dias} label='Dias' required maxLength={3} parse={onlyNumbersNonZeroLeft} />
        </Cell>
        <Cell size={4}>
          <CidSelectField name={name.cid10} label='CID10' renderGrupoCiapRelacionada={false} />
        </Cell>
        <AtestadoAssinaturaDigitalView
          name={name.assinadoDigitalmente}
          cidadaoId={cidadao.id}
          cidadaoEmail={emailCidadao}
          onEmailCidadaoChange={setIsSubmitDisabled}
          onDirtyChange={setIsEmailCidadaoDirty}
          hasCid10={!!formProps.values.cid10}
          style={styles.assinaturaDigital}
          emailCanceled={isEmailCidadaoCanceled}
          onEmailCancel={setIsEmailCidadaoCanceled}
        />
        <Cell size={12}>
          <VFlow>
            <AtestadoPadraoTextBox values={values} nomeUnidadeSaude={nomeUnidadeSaude} cidadao={cidadao} />
            <HFlow justifyContent='flex-end'>
              <Button size='small' onClick={() => handleCancel(formProps)}>
                Cancelar
              </Button>
              <Tooltip text={isSubmitDisabled && MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED}>
                <SubmitButton
                  throttleSubmit
                  type='button'
                  size='small'
                  kind='primary'
                  handleSubmit={formProps.handleSubmit}
                  disabled={isSubmitDisabled || isAssinando}
                  loading={isAssinando}
                >
                  Salvar atestado
                </SubmitButton>
              </Tooltip>
            </HFlow>
          </VFlow>
        </Cell>
        {isModalOpen && (
          <AtestadoConfirmarAssinaturaDigitalModal
            emailCidadao={emailCidadao}
            onConfirm={onConfirm}
            onCancel={onCancel}
            isAssinando={isAssinando}
            errorMessage={errorMessage}
            onAlertClose={onAlertClose}
            tipoAtestado={tipoAtestado}
            descricao={formProps.values.descricao}
          />
        )}
      </Fragment>
    )
  }

  return (
    <Form<AtestadoModel>
      render={renderForm}
      onSubmit={handleSubmitWrapper}
      validate={atestadoPadraoFormValidator(possuiCpfCns, dataAtendimento)}
      decorators={[cpfCnsDecorator]}
    />
  )
}

const styles = {
  naoPossuiContainer: css`
    margin-top: 1.75rem;
  `,
  assinaturaDigital: css`
    padding-left: 1rem;
  `,
}
